import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { CenterTeacher, CheckboxItemNode, Group, GroupMessageError, OnmatItinerary, ProgramEnum, StudentsGroup } from '@tekman/onmat/models';
import { EnvironmentService, UserService } from '@tekman/onmat/service/generic';
import * as profileActions from '@tekman/onmat/store/profile';
import { AlertService, HotjarService, StorageService } from '@tekman/shared/service';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';
import { GroupService } from '../service/group.service';
import * as groupsActions from './groups.actions';

@Injectable()
export class GroupsEffects {
  loadGroupsByAcademicYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(groupsActions.loadGroupsByAcademicYear),
      switchMap(academicYear => {
        return this.groupService.getUserGroupsByAcademicYear(academicYear.academicYearId).pipe(
          map(groups => {
            if (groups.length > 0) {
              if (this.env.settings?.isLomloeEnv) groups.map(g => (g.courseName = g.courseName === '3 AC' ? '3' : g.courseName));
              return groupsActions.loadGroupsSuccess({ groups });
            } else {
              return groupsActions.loadGroupsFailure({ error: { message: 'No tiene grupo' } });
            }
          }),
          catchError(error => of(groupsActions.loadGroupsFailure(error)))
        );
      })
    )
  );

  loadGroupsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(groupsActions.loadGroupsSuccess),
      map(({ groups }) => {
        let selectedGroupId = this.storageService.selectedGroupId;
        if (selectedGroupId) {
          const found = groups.find(group => group.id === selectedGroupId);
          if (found === undefined) {
            this.storageService.removeSelectedGroupId();
            selectedGroupId = groups[0].id;
          }
        } else {
          selectedGroupId = groups[0].id;
        }
        const selectedGroup = groups.find(group => group.id === selectedGroupId);
        return groupsActions.selectGroup({ selectedGroup });
      })
    )
  );

  loadEditableGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(groupsActions.loadEditableGroup),
      switchMap(({ id }) => {
        return this.groupService.getGroupById(id).pipe(
          map(group => groupsActions.loadEditableGroupSuccess({ group })),
          catchError(error => of(groupsActions.deleteTeacherFailure({ error })))
        );
      })
    )
  );

  updateStepCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(groupsActions.updateStepCompleted),
      switchMap(({ stepId, groupId, isCompleted }) => {
        return this.groupService.setStepComplete(groupId, stepId, isCompleted).pipe(
          map(() => groupsActions.updateStepCompletedSuccess({ stepId, isCompleted })),
          catchError(error => of(groupsActions.updateStepCompletedFailure({ error })))
        );
      })
    )
  );

  loadEditableTeachers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(groupsActions.loadEditableTeachers),
      switchMap(() =>
        this.groupService.getTeachersGroup().pipe(
          map((teachers: CenterTeacher[]) => groupsActions.loadEditableTeachersSuccess({ teachers })),
          catchError((error: HttpErrorResponse) =>
            of(groupsActions.loadEditableTeachersFailure({ error: { message: error?.message, type: 'teachers' } }))
          )
        )
      )
    )
  );

  saveGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(groupsActions.saveGroup),
      switchMap(({ groupCreation }) => {
        return (groupCreation.id ? this.groupService.editGroup(groupCreation) : this.groupService.createGroup(groupCreation)).pipe(
          map(group => {
            const successTeachers = group.teachers.filter(teachers => teachers.status !== 0);
            let entityGroup: Group;
            if (successTeachers.find(teacher => teacher.userName === this.userService.getUser().username)) {
              entityGroup = {
                id: group?.id,
                courseId: group.courseId,
                courseName: groupCreation.courseName,
                name: group.name,
                externalGroupInfo: group.externalGroupInfo,
                isLudificationActive: false,
              };
            }

            const newStudents = groupCreation.students.filter(students => !students.id);
            const failedStudents: StudentsGroup[] = [...group.students.filter(student => student.status === 0)];
            const successStudents: StudentsGroup[] = [
              ...group.students.filter(
                student => student.status === 3 && !!newStudents.find(newStudent => newStudent.userName === student.userName)
              ),
            ];
            const failedTeachers: CenterTeacher[] = [...group.teachers.filter(teachers => teachers.status === 0)] as CenterTeacher[];
            if (failedStudents.length || failedTeachers.length) {
              this.alertService.openAlert(this.translate.instant('f.alert.data-save-error'), false);
            } else {
              this.alertService.openAlert(this.translate.instant('f.alert.data-save-succes'));
            }
            return groupsActions.saveGroupSuccess({
              group: entityGroup,
              groupId: group.id,
              failedStudents: failedStudents.length ? failedStudents : null,
              successStudents: successStudents.length ? successStudents : null,
              failedTeachers: failedTeachers.length ? failedTeachers : null,
            });
          }),
          catchError(error => {
            // The API rejects with a 400 and ALREADY_EXISTING_GROUP_BY_EXTERNAL_GROUP_INFO_GIVEN
            // when the external class has already been imported into ONMAT.
            const existingGroupId = this.existingGroupIdFromError(error);
            if (existingGroupId) {
              return of(
                groupsActions.saveGroupAlreadyImported({
                  alreadyImportedGroup: {
                    externalGroupId: groupCreation.externalGroupInfo?.id,
                    groupId: existingGroupId,
                  },
                })
              );
            }
            return of(groupsActions.saveGroupFailure({ error }));
          })
        );
      })
    )
  );

  /**
   * Id of the ONMAT class that already holds the external class, returned by the API within the 400 body:
   * { errorCode: 'ALREADY_EXISTING_GROUP_BY_EXTERNAL_GROUP_INFO_GIVEN', existingGroupId: '<guid>', ... }
   * Returns null when the error is not an "already imported" rejection.
   */
  private existingGroupIdFromError(error: any): string {
    if (error?.status !== 400) return null;
    const body = error?.error ?? {};
    if (body.errorCode !== GroupMessageError.ALREADY_EXISTING_GROUP_BY_EXTERNAL_GROUP_INFO_GIVEN) return null;
    return body.existingGroupId ?? null;
  }

  removeGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(groupsActions.removeGroup),
      switchMap(({ groupId }) => {
        return this.groupService.deleteGroup(groupId).pipe(
          map(_ => {
            return groupsActions.removeGroupSuccess({ groupId });
          }),
          catchError(error => {
            return of(groupsActions.removeGroupFailure({ error }));
          })
        );
      })
    )
  );

  selectedGroupId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(groupsActions.selectGroup),
      map(({ selectedGroup }) => {
        this.storageService.selectedGroupId = selectedGroup.id;
        return groupsActions.selectedGroupSuccess({ selectedGroup });
      })
    )
  );

  selectedStudentId$ = createEffect(() =>
    combineLatest([
      this.actions$.pipe(ofType(groupsActions.selectStudent)),
      this.actions$.pipe(ofType(groupsActions.updatedTeamsSuccess)),
    ]).pipe(
      map(action => {
        const selectedStudentId = action[0].selectedStudent.id;
        let teams: CheckboxItemNode[] = action[1].teams;
        teams = teams.map(team => {
          team = { ...team, checked: team.id === selectedStudentId };
          team.children = team.children.map(student => {
            if (student.children) {
              const studentTeam = [];
              student.children.forEach(item => {
                studentTeam.push({ ...item, checked: item.id === selectedStudentId });
              });
              student = { ...student, children: studentTeam };
            } else {
              student = { ...student, checked: student.id === selectedStudentId };
            }
            return student;
          });
          return team;
        });
        return groupsActions.selectedStudentSuccess({ teams });
      }),
      catchError(error => of(groupsActions.selectedStudentFailure({ error })))
    )
  );

  loadStudentsGroup$ = createEffect(() =>
    this.actions$.pipe(ofType(groupsActions.selectedGroupSuccess)).pipe(
      switchMap(action => {
        if (this.userService.isTeacher()) {
          return this.groupService.getGroupById(action.selectedGroup.id).pipe(
            map(({ students }) => {
              students.sort((a, b) => {
                if (a.lastName.toLowerCase() !== b.lastName.toLowerCase()) {
                  return a.lastName.localeCompare(b.lastName);
                } else {
                  return a.name.localeCompare(b.name);
                }
              });
              return groupsActions.loadStudentsGroupSuccess({ groupId: action.selectedGroup.id, students });
            }),
            catchError(error => of(groupsActions.loadStudentsGroupFailure({ error })))
          );
        } else {
          return new Observable<Action>();
        }
      })
    )
  );

  teachers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(groupsActions.loadTeachers),
      switchMap(action =>
        this.groupService.getCenterTeachers(action.academicYearId).pipe(
          map((teachers: CenterTeacher[]) => groupsActions.loadTeachersSuccess({ teachers })),
          catchError((error: HttpErrorResponse) =>
            of(groupsActions.loadTeachersFailure({ error: { message: error?.message, type: 'teachers' } }))
          )
        )
      )
    )
  );

  deleteTeacher$ = createEffect(() =>
    this.actions$.pipe(
      ofType(groupsActions.deleteTeacher),
      switchMap(({ teacherId }) => {
        return this.groupService.deleteTeacher(teacherId).pipe(
          map(() => {
            this.alertService.openAlert(this.translate.instant('f.groups-teachers.delete.success'));
            return groupsActions.loadTeachers({ academicYearId: this.localStorage.selectedAcademicYear });
          }),
          catchError(error => {
            return of(groupsActions.deleteTeacherFailure({ error }));
          })
        );
      })
    )
  );

  hotjar$ = createEffect(() =>
    combineLatest([
      this.actions$.pipe(ofType(profileActions.loadProfileSuccess)),
      this.actions$.pipe(ofType(groupsActions.selectedGroupSuccess)),
    ]).pipe(
      switchMap(action => {
        const courseName = action[1]?.selectedGroup?.courseName;
        return this.hotjarService.setInfoHotjar(action[0].profile, action[0].userInfo, courseName).pipe(
          map(() => groupsActions.hotjarSuccess()),
          catchError(error => of(groupsActions.hotjarFailure({ error })))
        );
      })
    )
  );

  itinerary$ = createEffect(() =>
    this.actions$.pipe(ofType(groupsActions.selectedGroupSuccess)).pipe(
      switchMap(({ selectedGroup }: { selectedGroup: Group }) => {
        return this.groupService.getGroupItinerary(selectedGroup.id).pipe(
          map((_itinerary: OnmatItinerary) =>
            groupsActions.loadItinerarySuccess({
              groupId: selectedGroup.id,
              itinerary: _itinerary,
            })
          ),
          catchError((error: any) => of(groupsActions.loadItineraryFailure({ error })))
        );
      })
    )
  );

  loadProgress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(groupsActions.selectedGroupSuccess),
      switchMap(action => {
        if (action.selectedGroup.programId === ProgramEnum.ONMAT) {
          return this.groupService.loadUserProgress(action.selectedGroup.id).pipe(
            map((progress: string) => groupsActions.loadProgressSuccess({ progress })),
            catchError(() => of(groupsActions.loadProgressSuccess({ progress: undefined })))
          );
        } else {
          return of(groupsActions.loadProgressSuccess({ progress: undefined }));
        }
      })
    )
  );

  rollbackPeriodItineraries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(groupsActions.rollbackPeriodItineraries),
      switchMap((action: { periodItinerariesId: string; selectedGroupId: string }) =>
        this.groupService.rollbackPeriodItineraries(action.periodItinerariesId, action.selectedGroupId).pipe(
          map(() =>
            groupsActions.rollbackPeriodItinerariesSuccess({
              periodItinerariesId: action.periodItinerariesId,
              selectedGroupId: action.selectedGroupId,
            })
          ),
          catchError((error: HttpErrorResponse) =>
            of(groupsActions.rollbackPeriodItinerariesFailure({ error: { message: error?.message } }))
          )
        )
      )
    )
  );

  updateProgress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(groupsActions.updateProgress),
      switchMap(action =>
        this.groupService.updateUserProgress(action.groupId, action.stepId).pipe(
          map(() => groupsActions.loadProgressSuccess({ progress: action.stepId })),
          catchError(() => of(groupsActions.loadProgressSuccess({ progress: undefined })))
        )
      )
    )
  );

  saveTeachers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(groupsActions.saveTeachers),
      concatMap(({ teachers }) => {
        return this.groupService.saveTeachers(teachers).pipe(
          switchMap(teachersGroup => {
            const failedTeachers = teachersGroup.filter(teacher => teacher.status === 0);
            if (failedTeachers.length === 0) {
              return [groupsActions.loadTeachers({ academicYearId: this.localStorage.selectedAcademicYear })];
            } else {
              return [
                groupsActions.saveTeachersSuccess({
                  failedTeachers: failedTeachers,
                }),
                groupsActions.loadTeachers({ academicYearId: this.localStorage.selectedAcademicYear }),
              ];
            }
          }),
          catchError(error => {
            return of(groupsActions.saveTeachersFailure({ error }));
          })
        );
      })
    )
  );

  updatePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(groupsActions.updatePassword),
      switchMap(action =>
        this.groupService.updatePassword(action.studentId, action.password).pipe(
          map(() => {
            this.alertService.openAlert(this.translate.instant('f.alert.data-save-succes'));
            return groupsActions.updatePasswordSuccess();
          }),
          catchError(error => {
            this.alertService.openAlert(this.translate.instant('f.alert.data-save-error'), false);

            return of(groupsActions.updatePasswordFailure({ error }));
          })
        )
      )
    )
  );

  updateStudentInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(groupsActions.updateStudentInfo),
      switchMap(action =>
        this.groupService.updateStudentInfo(action.studentId, action.name, action.lastName).pipe(
          map(() => {
            this.alertService.openAlert(this.translate.instant('f.alert.data-save-succes'));
            return groupsActions.updateStudentInfoSuccess();
          }),
          catchError(error => {
            this.alertService.openAlert(this.translate.instant('f.alert.data-save-error'), false);
            return of(groupsActions.updateStudentInfoFailure({ error }));
          })
        )
      )
    )
  );

  deleteStudent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(groupsActions.deleteStudent),
      switchMap(action =>
        this.groupService.deleteStudent(action.groupId, action.studentId).pipe(
          map(() => {
            this.alertService.openAlert(this.translate.instant('f.alert.data-save-succes'));
            return groupsActions.deleteStudentSuccess({ id: action.studentId });
          }),
          catchError(error => {
            this.alertService.openAlert(this.translate.instant('f.alert.data-save-error'), false);
            return of(groupsActions.updateStudentInfoFailure({ error }));
          })
        )
      )
    )
  );
  inviteStudents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(groupsActions.inviteStudents),
      switchMap(action =>
        this.groupService.inviteStudents(action.groupId, action.userNames).pipe(
          map(students => {
            this.alertService.openAlert(this.translate.instant('f.alert.data-save-succes'));
            const failedStudents: StudentsGroup[] = [...students.filter(student => student.status !== 2 && student.status !== 3)];
            const successStudents: StudentsGroup[] = [...students.filter(student => student.status === 2 || student.status === 3)];
            return groupsActions.inviteStudentSuccess({ students: successStudents, failedStudents: failedStudents });
          }),
          catchError(error => {
            this.alertService.openAlert(this.translate.instant('f.alert.data-save-error'), false);
            return of(groupsActions.updateStudentInfoFailure({ error }));
          })
        )
      )
    )
  );

  addStepToItinerary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(groupsActions.addStepToItinerary),
      switchMap(action =>
        this.groupService.updateItinerarySteps(action.itineraryId, action.stepId, action.groupId).pipe(
          map(() => {
            this.alertService.openAlert(this.translate.instant('f.content.step_added'));
            return groupsActions.addStepToItinerarySuccess();
          }),
          catchError(error => of(groupsActions.addStepToItineraryFailure({ error })))
        )
      )
    )
  );

  deleteStepInItinerary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(groupsActions.deleteStepInItinerary),
      switchMap(action =>
        this.groupService.deleteItinerarySteps(action.itineraryId, action.stepId, action.groupId).pipe(
          map(() => {
            this.alertService.openAlert(this.translate.instant('f.content.step_deleted'));
            return groupsActions.deleteStepInItinerarySuccess();
          }),
          catchError(error => of(groupsActions.deleteStepInItineraryFailure({ error })))
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly groupService: GroupService,
    private readonly storageService: StorageService,
    private readonly userService: UserService,
    private readonly translate: TranslateService,
    private readonly env: EnvironmentService,
    private readonly alertService: AlertService,
    private readonly hotjarService: HotjarService,
    private readonly localStorage: StorageService
  ) {}
}
